/**
 * Local storage for Beoordelen "concept" drafts.
 *
 * A concept is the PL's in-progress employee selection (radio selection
 * per role + extra comparison employees) for one prospect, stored in
 * localStorage scoped to the current user (email) and prospect. It is
 * deliberately local-only: nothing here touches the server.
 */

const PREFIX = 'beoordelen-concept'

/**
 * Build the localStorage key for a concept.
 * @param {string|null|undefined} email - Current user's email.
 * @param {string|number} prospectId - Prospect the concept belongs to.
 * @returns {string} localStorage key.
 */
export function conceptStorageKey(email, prospectId) {
  return `${PREFIX}:${email || 'anon'}:${prospectId}`
}

/**
 * Store a concept payload under the given key.
 * @param {string} key - Key from `conceptStorageKey`.
 * @param {object} payload - `{ savedAt, plSelections, comparisonEmployees }`.
 * @returns {boolean} True when the write succeeded.
 */
export function saveConcept(key, payload) {
  try {
    localStorage.setItem(key, JSON.stringify(payload))
    return true
  } catch (error) {
    console.error('Failed to save concept to localStorage:', error)
    return false
  }
}

/**
 * Load a concept payload for the given key.
 * Returns null when nothing is stored, the JSON is malformed, or the
 * stored value is not a valid concept payload shape.
 * @param {string} key - Key from `conceptStorageKey`.
 * @returns {object|null} `{ savedAt, plSelections, comparisonEmployees }` or null.
 */
export function loadConcept(key) {
  try {
    const raw = localStorage.getItem(key)
    if (!raw) return null
    const payload = JSON.parse(raw)
    if (!payload || typeof payload !== 'object') return null
    if (typeof payload.plSelections !== 'object' || payload.plSelections === null) return null
    if (typeof payload.comparisonEmployees !== 'object' || payload.comparisonEmployees === null) {
      return null
    }
    return payload
  } catch (error) {
    console.error('Failed to load concept from localStorage:', error)
    return null
  }
}

/**
 * Remove a concept payload for the given key. Safe to call when the key
 * does not exist.
 * @param {string} key - Key from `conceptStorageKey`.
 */
export function clearConcept(key) {
  try {
    localStorage.removeItem(key)
  } catch (error) {
    console.error('Failed to clear concept from localStorage:', error)
  }
}
